import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";
import { planDisplayName } from "@/lib/planMarketing";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import PlanCatalogPicker from "@/components/PlanCatalogPicker";
import MockCheckoutPanel from "@/components/MockCheckoutPanel";
import {
  changeOrgPlan,
  fetchPlanCatalog,
} from "@/lib/billingApi";
import { toastSuccess } from "@/lib/toast";
import { useConfirm } from "@/components/ConfirmDialogProvider";

function formatBytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function Meter({ label, used, limit, formatUsed, formatLimit }) {
  const unlimited = limit == null;
  const pct = unlimited
    ? 0
    : Math.min(100, (100 * (Number(used) || 0)) / Math.max(1, Number(limit)));
  const near = !unlimited && pct >= 80;
  const at = !unlimited && pct >= 100;

  return (
    <div className="space-y-2">
      <div className="flex justify-between gap-3 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span
          className={cn(
            "font-medium tabular-nums shrink-0",
            at && "text-destructive",
            near && !at && "text-amber-700 dark:text-amber-400"
          )}
        >
          {formatUsed(used)}
          {" / "}
          {unlimited ? "Unlimited" : formatLimit(limit)}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full transition-all",
            at ? "bg-destructive" : near ? "bg-amber-500" : "bg-primary",
            unlimited && "w-0"
          )}
          style={unlimited ? undefined : { width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

const MODULE_LABELS = [
  { key: "kanban", label: "Team boards (kanban)" },
  { key: "digests", label: "Digests" },
  { key: "social_publish", label: "Social publish" },
  { key: "sso", label: "SSO" },
  { key: "api_access", label: "API access" },
  { key: "audit", label: "Audit log (view & export)" },
  { key: "partner_spaces", label: "Partner spaces (cross-org channels)" },
];

/**
 * Org settings → Plan & usage (admins only).
 */
const OrgPlanUsagePanel = ({ orgId, accessToken }) => {
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snap, setSnap] = useState(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [nextPlan, setNextPlan] = useState("free");
  const [checkoutId, setCheckoutId] = useState(null);
  const [changeBusy, setChangeBusy] = useState(false);
  const [changeError, setChangeError] = useState(null);
  const [lastReceipt, setLastReceipt] = useState(null);

  const load = useCallback(async () => {
    if (!orgId || !accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/org/${orgId}/entitlements`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setSnap(res.data || null);
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message || "Failed to load plan and usage"
      );
      setSnap(null);
    } finally {
      setLoading(false);
    }
  }, [orgId, accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const openChange = async () => {
    setChangeError(null);
    setCheckoutId(null);
    setNextPlan(snap?.plan === "enterprise" ? "business" : snap?.plan || "free");
    setChangeOpen(true);
    try {
      const rows = await fetchPlanCatalog(accessToken);
      setCatalog(rows);
    } catch (err) {
      setChangeError(
        err.response?.data?.message || "Could not load plan catalog"
      );
    }
  };

  const applyChange = async () => {
    if (changeBusy) return;
    if (nextPlan === snap?.plan) {
      setChangeOpen(false);
      return;
    }
    if (nextPlan === "free") {
      const ok = await confirm({
        title: "Downgrade to Free?",
        description:
          "Limits will drop to the Free plan immediately. You can upgrade again anytime with demo checkout.",
        confirmLabel: "Downgrade",
      });
      if (!ok) return;
      setChangeBusy(true);
      setChangeError(null);
      try {
        const data = await changeOrgPlan(accessToken, orgId, { plan: "free" });
        setLastReceipt(null);
        toastSuccess(data.confirmation_message || "Plan updated to Free");
        setChangeOpen(false);
        await load();
      } catch (err) {
        setChangeError(
          err.response?.data?.message || err.message || "Could not change plan"
        );
      } finally {
        setChangeBusy(false);
      }
      return;
    }
    if (!checkoutId) {
      setChangeError("Complete demo checkout for this plan first");
      return;
    }
    setChangeBusy(true);
    setChangeError(null);
    try {
      const data = await changeOrgPlan(accessToken, orgId, {
        plan: nextPlan,
        checkout_session_id: checkoutId,
      });
      setLastReceipt(data.receipt_id || null);
      toastSuccess(data.confirmation_message || "Plan updated");
      setChangeOpen(false);
      await load();
    } catch (err) {
      setChangeError(
        err.response?.data?.message || err.message || "Could not change plan"
      );
    } finally {
      setChangeBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl space-y-6">
        <p className="text-sm text-muted-foreground">Loading plan & usage…</p>
      </div>
    );
  }

  if (error || !snap) {
    return (
      <div className="max-w-3xl space-y-6">
        <p className="text-sm text-destructive">{error || "Unavailable"}</p>
      </div>
    );
  }

  const usage = snap.usage || {};
  const modules = snap.modules || {};
  const catalogEntry = catalog.find((c) => c.plan === nextPlan);
  const needsCheckout = nextPlan === "pro" || nextPlan === "business";

  return (
    <div className="max-w-3xl space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">Plan & usage</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Current plan for this workplace and how much of each limit is in
            use.
          </p>
        </div>
        <Button type="button" size="sm" onClick={openChange}>
          Change plan
        </Button>
      </div>

      <div className="rounded-lg border border-border px-4 py-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Current plan
        </p>
        <p className="mt-1 text-2xl font-semibold tracking-tight">
          {planDisplayName(snap.plan)}
        </p>
        {lastReceipt ? (
          <p className="mt-1 text-xs font-mono text-muted-foreground">
            Last mock receipt: {lastReceipt}
          </p>
        ) : null}
        {usage.period_ym ? (
          <p className="mt-1 text-xs text-muted-foreground">
            AI catch-up counts reset each calendar month (UTC). This period:{" "}
            {usage.period_ym}
          </p>
        ) : null}
      </div>

      <div className="space-y-5">
        <Meter
          label="Members"
          used={usage.seats_used}
          limit={snap.seat_limit}
          formatUsed={(n) => String(Number(n) || 0)}
          formatLimit={(n) => String(n)}
        />
        <Meter
          label="Storage"
          used={usage.storage_used_bytes}
          limit={snap.storage_quota_bytes}
          formatUsed={formatBytes}
          formatLimit={formatBytes}
        />
        {usage.storage_breakdown ? (
          <ul className="ml-1 space-y-1 border-l border-border pl-4 text-xs text-muted-foreground">
            <li className="flex justify-between gap-3">
              <span>Team files</span>
              <span className="tabular-nums">
                {formatBytes(usage.storage_breakdown.team_files_bytes)}
              </span>
            </li>
            <li className="flex justify-between gap-3">
              <span>Post attachments</span>
              <span className="tabular-nums">
                {formatBytes(usage.storage_breakdown.post_attachments_bytes)}
              </span>
            </li>
            <li className="flex justify-between gap-3">
              <span>Comment attachments</span>
              <span className="tabular-nums">
                {formatBytes(
                  usage.storage_breakdown.comment_attachments_bytes
                )}
              </span>
            </li>
            <li className="flex justify-between gap-3">
              <span>Message media</span>
              <span className="tabular-nums">
                {formatBytes(usage.storage_breakdown.dm_media_bytes)}
              </span>
            </li>
            {usage.storage_breakdown.partner_media_bytes ? (
              <li className="flex justify-between gap-3">
                <span>Partner channel media (your half)</span>
                <span className="tabular-nums">
                  {formatBytes(usage.storage_breakdown.partner_media_bytes)}
                </span>
              </li>
            ) : null}
          </ul>
        ) : null}
        <Meter
          label="Catch-up summaries (this month)"
          used={usage.ai_catchup_used}
          limit={snap.ai_catchup_monthly}
          formatUsed={(n) => String(Number(n) || 0)}
          formatLimit={(n) => String(n)}
        />
        <Meter
          label="Team boards (kanban on)"
          used={usage.kanban_teams_used}
          limit={snap.kanban_team_limit}
          formatUsed={(n) => String(Number(n) || 0)}
          formatLimit={(n) => String(n)}
        />
      </div>

      <div>
        <h4 className="text-sm font-semibold">Included on this plan</h4>
        <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
          {MODULE_LABELS.map(({ key, label }) => {
            const on = Boolean(modules[key]);
            return (
              <li
                key={key}
                className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm"
              >
                <span>{label}</span>
                <span
                  className={cn(
                    "text-xs font-medium",
                    on ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {on ? "Included" : "Not on this plan"}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="rounded-lg border border-border/80 bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        <p>
          Checkout is demo-only (no real charges). Enterprise stays contact
          sales / Suite. See{" "}
          <Link
            to="/about"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            About Circa
          </Link>{" "}
          for packaging overview.
        </p>
      </div>

      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change plan</DialogTitle>
            <DialogDescription>
              Free applies immediately. Pro and Business use demo checkout.
            </DialogDescription>
          </DialogHeader>
          {changeError && (
            <Alert variant="destructive">
              <AlertDescription>{changeError}</AlertDescription>
            </Alert>
          )}
          <PlanCatalogPicker
            catalog={catalog}
            selectedPlan={nextPlan}
            onSelect={(p) => {
              setNextPlan(p);
              setCheckoutId(null);
              setChangeError(null);
            }}
            disabled={changeBusy}
          />
          {needsCheckout && nextPlan !== snap.plan && (
            <MockCheckoutPanel
              accessToken={accessToken}
              plan={nextPlan}
              catalogEntry={catalogEntry}
              disabled={changeBusy}
              onSucceeded={(data) => {
                setCheckoutId(data.session_id);
                setChangeError(null);
              }}
            />
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setChangeOpen(false)}
              disabled={changeBusy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={applyChange}
              disabled={
                changeBusy ||
                nextPlan === snap.plan ||
                (needsCheckout && !checkoutId)
              }
            >
              {changeBusy ? "Updating…" : "Apply plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgPlanUsagePanel;
